/**
 * /reset/confirm — consume the magic-link token and set a new password.
 *
 * The link from email lands here as ``/reset?u=<id>&t=<token>``.
 * We read both from the query string, validate the new password,
 * and POST to /password-reset/confirm.
 */

import { type FormEvent, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { authApi, type AuthApiError } from "../../auth/api";

const MIN_PASSWORD_LENGTH = 12;

export function PasswordResetConfirm() {
  const location = useLocation();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const { userId, token } = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      userId: params.get("u") ?? "",
      token: params.get("t") ?? "",
    };
  }, [location.search]);

  if (!userId || !token) {
    return (
      <div className="auth-shell">
        <div className="auth-card">
          <h1 className="auth-title">Invalid reset link</h1>
          <p className="auth-subtle">
            The link is missing required information. Request a new
            reset email and try again.
          </p>
          <div className="auth-links">
            <Link to="/reset/request">Request a new link</Link>
          </div>
        </div>
      </div>
    );
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(
        `Password must be at least ${MIN_PASSWORD_LENGTH} characters long.`,
      );
      return;
    }
    setBusy(true);
    try {
      await authApi.passwordResetConfirm(userId, token, password);
      setDone(true);
    } catch (exc) {
      const apiError = exc as AuthApiError;
      setError(
        apiError.status === 400
          ? "Reset link is invalid or has expired."
          : (apiError.message ?? "Could not set the new password."),
      );
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div className="auth-shell">
        <div className="auth-card">
          <h1 className="auth-title">Password updated</h1>
          <p className="auth-subtle">
            Sign in with your new password to continue.
          </p>
          <button
            type="button"
            className="auth-primary"
            onClick={() => navigate("/login", { replace: true })}
          >
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-shell">
      <form className="auth-card" onSubmit={onSubmit} noValidate>
        <h1 className="auth-title">Choose a new password</h1>
        <label className="auth-field">
          <span className="auth-label">New password</span>
          <input
            type="password"
            autoComplete="new-password"
            required
            minLength={MIN_PASSWORD_LENGTH}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>
        <label className="auth-field">
          <span className="auth-label">Confirm new password</span>
          <input
            type="password"
            autoComplete="new-password"
            required
            minLength={MIN_PASSWORD_LENGTH}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </label>
        {error !== null && (
          <div className="auth-error" role="alert">
            {error}
          </div>
        )}
        <button type="submit" className="auth-primary" disabled={busy}>
          {busy ? "Updating..." : "Update password"}
        </button>
      </form>
    </div>
  );
}
